import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["arrowColor", "arrowSize", "className", "content", "Content", "renderContent", "onCloseComplete", "onCloseStart", "onKeyDown", "onOpenComplete", "onOpenStart", "open", "style", "use", "animate", "showCloseButton", "onOpenChange", "width"];
import classNames from 'classnames';
import { POPOVER_TRANSITION_TIMING } from 'HubStyleTokens/times';
import PropTypes from 'prop-types';
import { forwardRef, useRef, useState, isValidElement } from 'react';
import devLogger from 'react-utils/devLogger';
import styled, { css } from 'styled-components';
import * as PopoverContext from '../../context/PopoverContext';
import SyntheticEvent from '../../core/SyntheticEvent';
import TwoWayTransition from '../../transitions/TwoWayTransition';
import UIPopoverArrow, { ARROW_SIZES } from './UIPopoverArrow';
import UIPopoverBody from '../UIPopoverBody';
import UIPopoverFooter from '../UIPopoverFooter';
import UIPopoverHeader from '../UIPopoverHeader';
import { USE_CLASSES } from '../utils/Use';

/** @internal */

/** @internal */

/** @internal */

/** @internal */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isReactNode = node => typeof node !== 'object' || /*#__PURE__*/isValidElement(node) || Array.isArray(node);
const transitionDuration = parseInt(POPOVER_TRANSITION_TIMING, 10);
const renderStructuredContent = ({
  content,
  onOpenChange,
  showCloseButton,
  headerRef
}) => {
  if (isReactNode(content)) {
    return null;
  }
  const {
    header,
    body,
    footer,
    step
  } = content;
  return [header && /*#__PURE__*/_jsx(UIPopoverHeader, {
    showCloseButton: showCloseButton,
    onOpenChange: onOpenChange,
    ref: headerRef,
    children: header
  }, "header"), body && /*#__PURE__*/_jsx(UIPopoverBody, {
    children: body
  }, "body"), (footer || step) && /*#__PURE__*/_jsxs(UIPopoverFooter, {
    hasStructuredFooter: !!footer,
    hasStep: !!step,
    children: [step, footer]
  }, "footer")];
};
const transformOriginMixin = css(["", ";", ";"], ['top', 'bottom', 'left', 'right'].map(side => `.private-popover-attached-${side} & { transform-origin: ${side} }`), ['top', 'bottom'].map(ySide => ['left', 'right'].map(xSide => `.private-popover-attached-${ySide}.private-popover-attached-${xSide} & { transform-origin: ${ySide} ${xSide} }`)));
const PopoverTransitionWrapper = styled(TwoWayTransition.PlainWrapper).withConfig({
  displayName: "UIPopoverContent__PopoverTransitionWrapper"
})(["transition-property:", ";transition-duration:", "ms;opacity:", ";transform:", ";", ";"], ({
  transitioning
}) => transitioning ? 'opacity, transform' : 'none', ({
  duration
}) => duration, ({
  open
}) => open ? '1' : '0', ({
  open,
  transitioning
}) => {
  if (open && !transitioning) return 'none'; /* IE bugfix: #5368 */
  return open ? 'scale(1)' : 'scale(.75)';
}, transformOriginMixin);
const UIPopoverContent = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      arrowColor,
      arrowSize,
      className,
      content,
      Content,
      renderContent,
      onCloseComplete,
      onCloseStart,
      onKeyDown,
      onOpenComplete,
      onOpenStart,
      open,
      style,
      use,
      animate,
      showCloseButton,
      onOpenChange,
      width
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [openComplete, setOpenComplete] = useState(false);
  const computedArrowParams = ARROW_SIZES[arrowSize];
  const headerRef = useRef(null);
  let renderedContent = content;
  if (open) {
    if (Content) {
      renderedContent = /*#__PURE__*/_jsx(Content, {});
    } else if (renderContent) {
      renderedContent = renderContent();
    }
  }
  const computedOpen = open && !!renderedContent;
  if (process.env.NODE_ENV !== 'production') {
    if (showCloseButton && (isReactNode(renderedContent) || !renderedContent.header)) {
      devLogger.warn({
        message: `UIPopover: \`showCloseButton={true}\` requires you to provide the \`content\` prop as an object with a \`header\`.`,
        key: `UIPopover: showCloseButton`
      });
    }
  }
  const headerHeight = headerRef.current && headerRef.current.clientHeight || undefined;
  const renderedPopover = computedOpen && /*#__PURE__*/_jsx(PopoverContext.Provider, {
    value: {
      use
    },
    children: /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
      className: classNames(className, USE_CLASSES[use], 'private-popover uiPopover'),
      "data-open-complete": openComplete,
      onKeyDown: evt => {
        if (onKeyDown) onKeyDown(evt);
        if (evt.key === 'Escape' && onOpenChange) onOpenChange(SyntheticEvent(false));
      },
      style: width ? Object.assign({
        maxWidth: width,
        width
      }, style) : style,
      ref: ref,
      children: [/*#__PURE__*/_jsx(UIPopoverArrow, {
        color: arrowColor,
        width: computedArrowParams.width,
        popoverUse: use,
        headerHeight: headerHeight
      }), /*#__PURE__*/_jsx("div", {
        className: "private-popover__inner",
        children: isReactNode(renderedContent) ? renderedContent : renderStructuredContent({
          content: renderedContent,
          onOpenChange,
          showCloseButton,
          headerRef
        })
      })]
    }))
  });
  return /*#__PURE__*/_jsx(TwoWayTransition, {
    onCloseComplete: onCloseComplete,
    onCloseStart: () => {
      setOpenComplete(false);
      if (onCloseStart) onCloseStart();
    },
    onOpenComplete: () => {
      setOpenComplete(true);
      if (onOpenComplete) onOpenComplete();
    },
    onOpenStart: onOpenStart,
    transitionOnMount: true,
    duration: animate ? transitionDuration : 0,
    Wrapper: PopoverTransitionWrapper,
    children: renderedPopover
  });
});
const propTypes = {
  animate: PropTypes.bool.isRequired,
  arrowColor: PropTypes.string,
  arrowSize: PropTypes.oneOf(Object.keys(ARROW_SIZES)).isRequired,
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({
    header: PropTypes.node,
    body: PropTypes.node,
    footer: PropTypes.node,
    step: PropTypes.node
  })]),
  Content: PropTypes.elementType,
  renderContent: PropTypes.func,
  onCloseComplete: PropTypes.func,
  onCloseStart: PropTypes.func,
  onOpenChange: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onOpenStart: PropTypes.func,
  open: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  use: PropTypes.oneOf(Object.keys(USE_CLASSES)).isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
UIPopoverContent.propTypes = propTypes;
UIPopoverContent.displayName = 'UIPopoverContent';
export default UIPopoverContent;